/**
 * ContactQuickCreateDialog - Minimal dialog for quick contact creation
 *
 * Following the Quick Create Dialog pattern:
 * - Fields: First Name, Last Name, Email, Organization, Relationship
 * - When company is selected, copies company's relationships as defaults
 * - Redirects to detail page after creation for full editing
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Check } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { DialogForm, ValidatedEmailInput, ValidatedPhoneInput } from '../../../../shared/components/common';
import { useCreateContact } from '../hooks/useContacts';
import { useOrganization, useOrganizationDomainMatch } from '../hooks/useOrganizations';
import { OrganizationPicker } from './OrganizationPicker';
import { OrganizationDomainSuggestion } from './OrganizationDomainSuggestion';
import type { Contact, ContactCreate, Organization } from '../types';
import classificationConfig from '../../../../shared-config/business_classification.config.json';

const EMPTY_RELATIONSHIPS: string[] = [];

interface ContactQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill company from context */
  defaultCompanyId?: string;
  /** Pre-fill the first name field (e.g., from picker search text) */
  defaultFirstName?: string;
  /** Pre-fill the last name field (e.g., from picker search text) */
  defaultLastName?: string;
  /** Callback when contact is created (before navigation) */
  onCreated?: (contact: Contact) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-select these contact relationship types (e.g., ['lead'] or ['customer']) */
  defaultRelationships?: string[];
}

interface QuickCreateFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  organization_id?: string;
  contact_relationship: string[];
}

export function ContactQuickCreateDialog({
  open,
  onClose,
  defaultCompanyId,
  defaultFirstName = '',
  defaultLastName = '',
  onCreated,
  skipNavigation = false,
  defaultRelationships = EMPTY_RELATIONSHIPS,
}: ContactQuickCreateDialogProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateContact();
  const userHasEditedRelationships = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      title: '',
      organization_id: defaultCompanyId || undefined,
      contact_relationship: defaultRelationships,
    },
  });

  const contactRelationship = watch('contact_relationship');
  const emailValue = watch('email');
  const organizationId = watch('organization_id');
  const [dismissedDomain, setDismissedDomain] = useState('');

  // Suggest an existing organization matching the email domain (fred@acme.com
  // → acme.com). Only while the dialog is open and no organization is set yet.
  const { match: domainMatch, domain: matchedDomain } = useOrganizationDomainMatch(
    emailValue,
    open && !organizationId,
  );
  const showDomainSuggestion =
    !organizationId && !!domainMatch && matchedDomain !== dismissedDomain;

  // When the contact is being created within an organization (org pre-filled via
  // defaultCompanyId), fetch that org so the contact inherits its relationships.
  // The interactive picker path is handled separately by handleCompanySelected.
  const { data: defaultOrg } = useOrganization(open && defaultCompanyId ? defaultCompanyId : '');

  useEffect(() => {
    if (open) {
      reset({
        first_name: defaultFirstName,
        last_name: defaultLastName,
        email: '',
        phone: '',
        title: '',
        organization_id: defaultCompanyId || undefined,
        contact_relationship: defaultRelationships,
      });
      userHasEditedRelationships.current = false;
      setDismissedDomain('');
    }
  }, [open, defaultCompanyId, defaultFirstName, defaultLastName, defaultRelationships, reset]);

  // Inherit relationships from the pre-filled organization (default behavior:
  // a contact created within an org adopts the org's relationships). Skipped
  // once the user edits the relationships manually.
  useEffect(() => {
    if (
      open &&
      defaultCompanyId &&
      defaultOrg?.organization_relationship?.length &&
      !userHasEditedRelationships.current
    ) {
      const merged = Array.from(
        new Set([...defaultRelationships, ...defaultOrg.organization_relationship]),
      );
      setValue('contact_relationship', merged);
    }
  }, [open, defaultCompanyId, defaultOrg, defaultRelationships, setValue]);

  const handleCompanySelected = (company: Organization | null) => {
    if (company && company.organization_relationship?.length > 0 && !userHasEditedRelationships.current) {
      // Copy company relationships as defaults, preserving any defaultRelationships
      const merged = Array.from(new Set([...defaultRelationships, ...company.organization_relationship]));
      setValue('contact_relationship', merged);
    }
  };

  const handleUseSuggestedOrganization = (company: Organization) => {
    setValue('organization_id', company.id);
    handleCompanySelected(company);
  };

  const toggleRelationship = (value: string) => {
    userHasEditedRelationships.current = true;
    const current = contactRelationship || [];
    if (current.includes(value)) {
      setValue('contact_relationship', current.filter(v => v !== value));
    } else {
      setValue('contact_relationship', [...current, value]);
    }
  };

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const createData: ContactCreate = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: data.phone?.trim() || undefined,
        title: data.title?.trim() || undefined,
        organization_id: data.organization_id || undefined,
        contact_relationship: data.contact_relationship.length > 0 ? data.contact_relationship : undefined,
      };

      const newContact = await createMutation.mutateAsync(createData);
      onCreated?.(newContact);
      onClose();

      if (!skipNavigation) {
        navigate(`/master-data/contacts/${newContact.id}`);
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('contacts.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">{t('contacts.firstName')}</Label>
            <Controller
              name="first_name"
              control={control}
              rules={{
                validate: (value, formValues) =>
                  (value || '').trim() || (formValues.last_name || '').trim()
                    ? true
                    : t('common:validation.firstOrLastNameRequired'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="first_name"
                  autoFocus
                  className={cn(errors.first_name && 'border-destructive')}
                />
              )}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive">{errors.first_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">{t('contacts.lastName')}</Label>
            <Controller
              name="last_name"
              control={control}
              rules={{
                validate: (value, formValues) =>
                  (value || '').trim() || (formValues.first_name || '').trim()
                    ? true
                    : t('common:validation.firstOrLastNameRequired'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  className={cn(errors.last_name && 'border-destructive')}
                />
              )}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        <ValidatedEmailInput
          name="email"
          control={control}
          label={t('contacts.email')}
        />

        <ValidatedPhoneInput
          name="phone"
          control={control}
          label={t('contacts.phone')}
        />

        <div className="space-y-2">
          <Label htmlFor="title">{t('contacts.jobTitle')}</Label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => <Input {...field} id="title" />}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('contacts.company')}</Label>
          <Controller
            name="organization_id"
            control={control}
            render={({ field }) => (
              <OrganizationPicker
                value={field.value || ''}
                onChange={(id) => field.onChange(id || undefined)}
                onCompanySelected={handleCompanySelected}
                defaultRelationships={contactRelationship}
                allowClear
              />
            )}
          />
          {showDomainSuggestion && domainMatch && (
            <OrganizationDomainSuggestion
              organization={domainMatch}
              domain={matchedDomain}
              onUse={handleUseSuggestedOrganization}
              onDismiss={() => setDismissedDomain(matchedDomain)}
            />
          )}
        </div>

        {/* Relationship Types - multi-toggle */}
        <div className="space-y-2">
          <Label>{t('common:businessClassification.contactRelationship.label')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {classificationConfig.relationship_types.map((type) => {
              const isSelected = contactRelationship?.includes(type.value);
              return (
                <Button
                  key={type.value}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleRelationship(type.value)}
                  className="gap-1"
                >
                  {isSelected && <Check className="h-3 w-3" />}
                  {t(`common:businessClassification.contactRelationship.${type.value}`)}
                </Button>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('contacts.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
